/*
TODO: Implement commented-out mobile code for ReceiptReview to be a truly
shared component.

SOURCE: screens/PlusScreen/Receipt/ReceiptView.tsx
*/

import React, { FC, useCallback } from 'react';
import {
  // Dimensions,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Linking,
} from 'react-native';

import {
  chargeDataFragment,
  installmentPlanDataFragment,
  paymentDataFragment,
} from 'apollo/queries/autogenerate/operations';
import {
  ChargeKind,
  InstallmentPlanKind,
} from 'apollo/queries/autogenerate/schemas';
import { AMZ_SUB_MERCHANTS_IDS } from 'constants/amazon';
import colors from 'shared/modules/common/constants/colors';
import typography from 'shared/modules/common/constants/typography';
import url from 'shared/modules/common/constants/url';
import {
  toFullWidth,
  convertInstallmentKindToNumber,
  isCancellationCharge,
} from 'shared/modules/common/utils/charge-utils';
import { UnavailableReasonCode } from 'shared/modules/usage/utils/utils';
import { formatYearMonthDate, formatToCurrency } from 'shared/utils/japanese';
import openBrowserAsync from 'shared/utils/open-browser-async';
import getTestID, { TestIDProps } from 'shared/utils/test-id';
import { convertAmazonPayTitle } from 'utils/amazon-pay-default-storename';

type Props = {
  shouldShowClose?: boolean;
  onClose?: () => void;
  message?: string;
  charge?: chargeDataFragment;
  payment?: paymentDataFragment | null;
  installmentPlan?: installmentPlanDataFragment | null;
  combiniFee?: number;
  orderNumber?: string | null;
  // Houkatsu approved and not Apple payment
  isConvertNpayEligible?: boolean;
};

export default function ReceiptView({
  shouldShowClose = false,
  onClose = () => {},
  message = '',
  charge,
  payment,
  installmentPlan,
  combiniFee = 0,
  orderNumber,
  isConvertNpayEligible = false,
}: Props) {
  const isAmazonPay =
    payment?.subMerchantId === AMZ_SUB_MERCHANTS_IDS.amazonPay;

  // If the storeName of Amazon Pay purchase starts with 'AMZ', should display default title
  const shouldConvertAmazonPayDefaultTitle =
    !!payment?.storeName && convertAmazonPayTitle(payment?.storeName);

  // If the the payment is Amazon Pay purchase, should display following title
  const amazonPayTitle = isAmazonPay
    ? payment?.storeName === null ||
      payment?.storeName === '<NO NAME>' ||
      payment?.storeName === undefined ||
      shouldConvertAmazonPayDefaultTitle
      ? 'Amazon Pay提携サイト'
      : payment?.storeName
    : null;

  // If the payment is Amazon Purchase besides Amazon Pay and the storeName is null, should display the default value 'Amazon.co.jp'
  const isAmazon =
    payment?.subMerchantId === AMZ_SUB_MERCHANTS_IDS.amazonPrime ||
    AMZ_SUB_MERCHANTS_IDS.amazonKindle ||
    AMZ_SUB_MERCHANTS_IDS.amazonMusic ||
    AMZ_SUB_MERCHANTS_IDS.amazonPharmacy ||
    AMZ_SUB_MERCHANTS_IDS.amazonPrimeVideo ||
    AMZ_SUB_MERCHANTS_IDS.amazonRetail ||
    AMZ_SUB_MERCHANTS_IDS.amazonMarketplace;

  const amazonTitle = isAmazon
    ? payment?.storeName === null || payment?.storeName === '<NO NAME>'
      ? 'Amazon.co.jp'
      : payment?.storeName
    : null;
  const isAmazonPrime =
    payment?.subMerchantId === AMZ_SUB_MERCHANTS_IDS.amazonPrime;

  // Display 'capturedAmount' for the Installment Receipt when it's captured AND when the 'amount' is greater than 'capturedAmount'.
  let amount = charge?.amount || 0;

  if (
    !charge &&
    !!payment?.capturedAmount &&
    payment.amount > payment.capturedAmount
  ) {
    amount = payment.capturedAmount;
  }

  if (
    (!charge && !!payment?.amount && !payment?.capturedAmount) ||
    (!charge &&
      !!payment?.capturedAmount &&
      payment?.amount <= payment?.capturedAmount)
  ) {
    amount = payment.amount;
  }

  const title =
    amazonPayTitle ?? charge?.description ?? amazonTitle ?? payment?.storeName;
  const subtitle = payment?.byWayOf;
  const createdAt = charge?.pushedAt ?? payment?.createdAt;

  const tax = payment?.order.tax ?? 0;
  const shipping = payment?.order.shipping ?? 0;
  const items = payment?.order.items ?? [];
  // Amazon order id
  const orderId = payment?.orderId;

  const chargeKind = charge?.kind ?? ChargeKind.CAPTURE;
  const faqUrl = url.amazonPrimeFaq;
  const cancellationCharge = isCancellationCharge(charge);

  const TextLink = () => {
    const onPressClick = useCallback(async () => {
      if (await Linking.canOpenURL(faqUrl)) {
        Linking.openURL(faqUrl);
      }
    }, [faqUrl]);

    return (
      <Text style={styles.primeLink} onPress={onPressClick}>
        こちらのFAQ
      </Text>
    );
  };

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.headerContainer}>
        <View style={{ flex: 1 }}>
          <ChargeIconView chargeKind={chargeKind} />
          <Text style={styles.headerTitle} {...getTestID('receipt_title')}>
            {title}
          </Text>
          {subtitle && (
            <Text
              style={styles.headerSubtitle}
              {...getTestID('receipt_sub_title')}
            >
              {subtitle}
            </Text>
          )}
          {/* For Amazon Pay payments, display division title */}
          {!!isAmazonPay && (
            <Text style={styles.headerDivisiontitle}>Amazon Pay</Text>
          )}
          <Text style={styles.transactionDate}>
            {!!payment && chargeKind !== ChargeKind.REFUND && '購入日 '}
            {!!createdAt && formatYearMonthDate(createdAt, 'en')}
          </Text>
          {!!orderNumber && (
            <Text style={styles.orderNumber}>ご注文番号 {orderNumber}</Text>
          )}
        </View>

        {shouldShowClose && (
          <View style={styles.rightContainer}>
            <TouchableOpacity onPress={onClose} {...getTestID('close_receipt')}>
              <Image
                source={require('shared/assets/images/icons/times@3x.png')}
                style={styles.iconImage}
              />
            </TouchableOpacity>
          </View>
        )}
      </View>

      {chargeKind !== ChargeKind.REFUND && (
        <>
          {items.length > 0 && <View style={styles.separator} />}

          {/* Items */}
          {items.map((item, index) => (
            <OrderItem
              key={`item-${index}`}
              title={item.title ?? item.description ?? '商品名未登録'}
              quantity={item.quantity}
              unitPrice={item.unitPrice}
              testID={`receipt_item_${index}`}
            />
          ))}
        </>
      )}
      <View style={styles.separator} />

      {!!orderId && (
        <View style={styles.orderIdContainer}>
          <Text style={typography.caption02}>Amazonの注文番号</Text>
          <Text style={typography.caption02}>{orderId}</Text>
        </View>
      )}

      {!!orderId && <View style={styles.separator} />}

      {/* Summary */}
      {!!tax && <OrderItem title="消費税" unitPrice={tax} variant="tax" />}
      {!!shipping && (
        <OrderItem title="送料" unitPrice={shipping} variant="shipping" />
      )}
      {(!!tax || !!shipping) && <View style={styles.separator} />}

      <OrderItem
        title={cancellationCharge ? 'キャンセル金額' : '合計'}
        unitPrice={amount}
        variant="total"
        testID="receipt_total_amount"
      />
      <UnavailableChargeMessageView
        charge={charge}
        message={message}
        installmentPlan={installmentPlan}
        isConvertNpayEligible={isConvertNpayEligible}
      />

      {!!combiniFee && (
        <CenteredMessageView
          message={`※ 別途コンビニ支払手数料${formatToCurrency(
            combiniFee
          )}お支払済み`}
        />
      )}

      <UncapturedChargeMessageView
        charge={charge}
        payment={payment}
        installmentPlan={installmentPlan}
      />

      {/* For Amazon Prime subscription, display a red alert box */}
      {!!isAmazonPrime && (
        <View style={styles.primeContainer}>
          <Text style={styles.primeItem}>※</Text>
          <Text style={styles.primeMessage}>
            Amazonプライム会費のお支払いをペイディに設定した覚えがない方は、
            <TextLink />
            をご確認ください。
          </Text>
        </View>
      )}

      <CancellationChargeMessageView charge={charge} />
    </View>
  );
}

const OrderItem = ({
  title,
  quantity = 1,
  unitPrice,
  variant = 'default',
  testID,
}: {
  title: string;
  quantity?: number;
  unitPrice: number;
  variant?: 'default' | 'tax' | 'shipping' | 'total';
} & TestIDProps) => {
  const itemText = variant === 'total' ? styles.itemTextBold : styles.itemText;
  const paddingVertical = variant === 'default' ? 12 : 4;

  return (
    <View style={[styles.itemContainer, { paddingVertical }]}>
      <Text style={itemText}>
        {quantity > 1 ? `${title} x ${quantity}` : title}
      </Text>

      <View style={styles.itemDetailContainer}>
        <Text style={itemText} {...getTestID(testID ?? title)}>
          {formatToCurrency(unitPrice * quantity)}
        </Text>
      </View>
    </View>
  );
};

const NoticeView: FC = ({ children }) => {
  return (
    <View style={styles.noticeContainer}>
      <Text style={styles.noticeIcon}>※</Text>
      {children}
    </View>
  );
};

const CenteredMessageView = ({ message }: { message: string }) => {
  return (
    <View style={styles.centeredMessageContainer}>
      <Text style={styles.centeredMessageText}>{message}</Text>
    </View>
  );
};

// TODO: Use for mobile only
// export const ReceiptModalView = ({
//   visible,
//   onClose,
//   payment,
// }: {
//   visible: boolean;
//   onClose: () => void;
//   payment: paymentDataFragment;
// }) => {
//   return visible ? (
//     <Modal transparent={true} visible={visible} onRequestClose={onClose}>
//       <View style={styles.modalBackground}>
//         <View style={styles.modalContainer}>
//           <ScrollView bounces={false} style={styles.scrollView}>
//             <ReceiptView
//               payment={payment}
//               shouldShowClose={true}
//               onClose={onClose}
//             />
//           </ScrollView>
//         </View>
//       </View>
//     </Modal>
//   ) : null;
// };

export const ChargeIconView = ({ chargeKind }: { chargeKind: ChargeKind }) => {
  let iconTitle = '';
  let iconSource = require('shared/assets/images/icons/charge-kind/capture@3x.png');

  switch (chargeKind) {
    case ChargeKind.CASHBACK:
      iconSource = require('shared/assets/images/icons/charge-kind/cashback@3x.png');
      iconTitle = 'キャッシュバック';
      break;

    case ChargeKind.LATE_FEE:
      iconSource = require('shared/assets/images/icons/charge-kind/late-fee@3x.png');
      iconTitle = '遅延手数料';
      break;

    case ChargeKind.MULTI_PAY_CREDIT:
      iconSource = require('shared/assets/images/icons/charge-kind/multi-pay@3x.png');
      iconTitle = '分割支払額';
      break;

    case ChargeKind.MULTI_PAY_DEBIT:
      iconSource = require('shared/assets/images/icons/charge-kind/multi-pay@3x.png');
      iconTitle = '分割払いへ変更';
      break;

    case ChargeKind.REFUND:
      iconSource = require('shared/assets/images/icons/charge-kind/refund@3x.png');
      iconTitle = '返金';
      break;

    case ChargeKind.SETTLEMENT:
      iconSource = require('shared/assets/images/icons/charge-kind/settlement@3x.png');
      iconTitle = 'お支払い受領';
      break;

    case ChargeKind.MANUAL_CREDIT:
      iconSource = require('shared/assets/images/icons/charge-kind/manual-credit@3x.png');
      break;

    case ChargeKind.MANUAL_DEBIT:
      iconSource = require('shared/assets/images/icons/charge-kind/manual-debit@3x.png');
      break;
  }

  return (
    <View style={styles.iconContainer}>
      <Image source={iconSource} style={styles.iconImage} />
      <Text style={styles.iconTitle}>{iconTitle}</Text>
    </View>
  );
};

function UnavailableChargeMessageView({
  charge,
  message,
  installmentPlan,
  isConvertNpayEligible,
}: {
  charge?: chargeDataFragment;
  message: string;
  installmentPlan?: installmentPlanDataFragment | null;
  isConvertNpayEligible: boolean;
}) {
  if (message) {
    return (
      <NoticeView>
        <Text style={styles.noticeText}>{message}</Text>
      </NoticeView>
    );
  }

  /*
    Displays already converted message if the receipt was access via InstallmentPlanView.
  */
  if (installmentPlan) {
    const totalInstallments = convertInstallmentKindToNumber(
      installmentPlan.kind
    );

    return (
      <NoticeView>
        <Text
          style={styles.noticeText}
          {...getTestID('unavailable_message_already_converted')}
        >
          {`現在、${toFullWidth(totalInstallments)}回あと払いをご利用中です。`}
        </Text>
      </NoticeView>
    );
  }

  if (
    isConvertNpayEligible &&
    charge?.installmentPlanOptions &&
    charge.installmentPlanOptions.unavailable.length > 0
  ) {
    const threePayUnavailableOption = charge.installmentPlanOptions.unavailable.find(
      (option) => option.kind === InstallmentPlanKind.THREE_PAY
    );
    const sixPayUnavailableOption = charge.installmentPlanOptions.unavailable.find(
      (option) => option.kind === InstallmentPlanKind.SIX_PAY
    );
    const twelvePayUnavailableOption = charge.installmentPlanOptions.unavailable.find(
      (option) => option.kind === InstallmentPlanKind.TWELVE_PAY
    );

    // Already converted to Npay
    if (
      threePayUnavailableOption?.reason.code ===
        UnavailableReasonCode.ALREADY_CONVERTED ||
      sixPayUnavailableOption?.reason.code ===
        UnavailableReasonCode.ALREADY_CONVERTED ||
      twelvePayUnavailableOption?.reason.code ===
        UnavailableReasonCode.ALREADY_CONVERTED
    ) {
      return (
        <NoticeView {...getTestID('unavailable_message')}>
          <Text
            style={styles.noticeText}
            {...getTestID('unavailable_message_already_converted')}
          >
            {`現在、3・6・12回あと払いをご利用中です。`}
          </Text>
        </NoticeView>
      );
    } else if (
      threePayUnavailableOption?.reason.code ===
      UnavailableReasonCode.MERCHANT_DISABLED
    ) {
      return (
        <NoticeView {...getTestID('unavailable_message')}>
          <Text
            style={styles.noticeText}
            {...getTestID('unavailable_message_3pay_disabled')}
          >
            {`現在このお店では３回あと払いをご利用いただけません。詳しくは`}
            <TouchableOpacity
              onPress={() =>
                openBrowserAsync(
                  'https://support.paidy.com/hc/ja/articles/900004753343'
                )
              }
            >
              <Text style={[styles.noticeText, styles.link]}>FAQ</Text>
            </TouchableOpacity>
            {`をご覧ください。`}
          </Text>
        </NoticeView>
      );
    }
    // For web, since we don’t show 3-pay conversion eligibility info, we should not show 3-pay conversion ineligibility either.
  }

  return null;
}

/**
 *
 * This function displays a label for uncaptured 3/6-pay
 */
function UncapturedChargeMessageView({
  charge,
  payment,
  installmentPlan,
}: {
  charge?: chargeDataFragment;
  payment?: paymentDataFragment | null;
  installmentPlan?: installmentPlanDataFragment | null;
}) {
  if (charge || !payment || installmentPlan) {
    return null;
  }

  let label = '';

  if (payment.installmentPlanKind === InstallmentPlanKind.THREE_PAY) {
    label = 'ご利用確定後に３回あと払いに変更されます。';
  } else if (payment.installmentPlanKind === InstallmentPlanKind.SIX_PAY) {
    label = 'ご利用確定後に６回あと払いに変更されます。';
  } else if (payment.installmentPlanKind === InstallmentPlanKind.TWELVE_PAY) {
    label = 'ご利用確定後に12回あと払いに変更されます。';
  }

  if (!label) {
    return null;
  }

  return (
    <NoticeView>
      <Text style={styles.noticeText}>{label}</Text>
    </NoticeView>
  );
}

/*
  This function returns a message component that 
  payments were cancelled.
*/
function CancellationChargeMessageView({
  charge,
}: {
  charge: chargeDataFragment | undefined;
}) {
  if (!charge) {
    return null;
  }

  const cancellationCharge = isCancellationCharge(charge);

  if (!cancellationCharge) {
    return null;
  }

  const previousInstallmentOption =
    charge.installmentPlanOptions?.unavailable[0].reason.details.installments
      ?.length;
  const label = previousInstallmentOption
    ? toFullWidth(previousInstallmentOption) +
      `回あと払いがキャンセルされました。`
    : '';

  return (
    <NoticeView>
      <Text style={styles.noticeText}>{label}</Text>
    </NoticeView>
  );
}

const styles = StyleSheet.create({
  container: {
    width: '100%',
    backgroundColor: colors.white,
    padding: 24,
  },
  separator: {
    height: 1,
    backgroundColor: '#CFD4D8',
    marginVertical: 8,
  },
  headerContainer: {
    flexDirection: 'row',
    backgroundColor: colors.white,
    marginBottom: 8,
  },
  headerTitle: {
    ...typography.title03,
    fontSize: 16,
    paddingTop: 8,
    paddingBottom: 4,
  },
  headerSubtitle: {
    ...typography.caption02,
    paddingBottom: 8,
  },
  headerDivisiontitle: {
    ...typography.caption02,
    paddingTop: 12,
    paddingBottom: 16,
  },
  transactionDate: {
    ...typography.caption02,
    color: colors.greyDark,
  },
  orderNumber: {
    ...typography.caption02,
    color: colors.greyDark,
    marginTop: 16,
  },
  rightContainer: {
    flex: 1,
    alignItems: 'flex-end',
    flexDirection: 'column',
  },

  // Item
  itemContainer: {
    flexDirection: 'row',
    backgroundColor: colors.white,
  },
  itemDetailContainer: {
    marginStart: 8,
    alignItems: 'flex-end',
  },
  itemText: {
    flex: 1,
    ...typography.caption02,
    fontSize: 13,
    fontWeight: '400',
    color: colors.greyDark,
  },
  itemTextBold: {
    flex: 1,
    ...typography.title03,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: '700',
    color: colors.greyDark,
  },

  // Notice message box
  noticeContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderWidth: 0.5,
    borderRadius: 4,
    borderColor: colors.pinkDark,
    marginTop: 16,
    padding: 16,
    paddingRight: 32,
  },
  noticeIcon: {
    ...typography.caption02,
    color: colors.pinkDark,
    paddingRight: 8,
  },
  noticeText: {
    ...typography.caption02,
    color: colors.pinkDark,
  },
  link: {
    textDecorationLine: 'underline',
    marginBottom: -3,
  },

  // CenteredMessageView
  centeredMessageContainer: {
    marginTop: 16,
  },
  centeredMessageText: {
    ...typography.caption02,
    textAlign: 'center',
  },

  // Icon
  iconContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  iconTitle: {
    ...typography.title03,
    marginLeft: 8,
    color: colors.teal,
    fontSize: 16,
    lineHeight: 24,
    fontWeight: 'bold',
  },
  iconImage: {
    width: 24,
    height: 24,
  },
  // Amazon orderId
  orderIdContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
    marginBottom: 16,
  },
  // Message for Amazon Prime Subscription
  primeContainer: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 30,
    marginBottom: 10,
    borderColor: colors.pinkDark,
    borderWidth: 1,
    padding: 16,
    borderRadius: 4,
  },
  primeItem: {
    ...typography.caption02,
    color: colors.pinkDark,
    paddingRight: 11,
  },
  primeMessage: {
    ...typography.caption02,
    color: colors.pinkDark,
  },
  primeLink: {
    textDecorationLine: 'underline',
    marginBottom: -3,
    ...typography.caption02,
    color: colors.pinkDark,
  },
});
